import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ValuationCategory = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('valuation_categories');
  const [viewItem, setViewItem] = useState(null);
  const [valuationCategories, setValuationCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  useEffect(() => {
    loadValuationCategories();
  }, []);

  const loadValuationCategories = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/valuation-categories`);
      setValuationCategories(response.data || []);
    } catch (error) {
      console.error('Error loading valuation categories:', error);
      toast.error('Failed to load valuation categories');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (category = null) => {
    setEditingCategory(category);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      valuation_category_id: (formData.get('valuation_category_id') || '').toUpperCase(),
      description: formData.get('description')
    };

    try {
      if (editingCategory) {
        await axios.put(`${BACKEND_URL}/api/valuation-categories/${editingCategory.valuation_category_id}`, {
          description: data.description
        });
        toast.success('Valuation Category updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/valuation-categories`, data);
        toast.success('Valuation Category created successfully');
      }
      setIsDialogOpen(false);
      loadValuationCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save valuation category');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Valuation Categories</h1>
            <p className="text-sm text-gray-500 mt-1">Manage valuation category configurations</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Valuation Categories</h2>
            {writable && (<Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Valuation Category
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : valuationCategories.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No valuation categories found. Create one to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {valuationCategories.map((category) => (
                    <tr key={category.valuation_category_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{category.valuation_category_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{category.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <RecordActions doctype="valuation_categories" onView={() => setViewItem(category)} onEdit={() => handleOpenDialog(category)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Edit Valuation Category' : 'Create Valuation Category'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="valuation_category_id">Valuation Category ID * (1 character)</Label>
                <Input
                  id="valuation_category_id"
                  name="valuation_category_id"
                  defaultValue={editingCategory?.valuation_category_id || ''}
                  disabled={editingCategory !== null}
                  required
                  maxLength={1}
                  minLength={1}
                  pattern="[A-Za-z0-9]"
                  title="Must be exactly 1 alphanumeric character"
                  className="mt-1 uppercase"
                  placeholder="e.g., H, S, V"
                  onInput={(e) => {
                    e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 1);
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">Must be exactly 1 alphanumeric character</p>
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  name="description"
                  defaultValue={editingCategory?.description || ''}
                  required
                  maxLength={100}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingCategory ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default ValuationCategory;
